import { FC } from "react";

import { Box, Stack, useTheme } from "@mui/material";
import { Nullable } from "@simplyk/common";

import { useHeaderScroll } from "./useHeaderScroll";

import { useTranslate } from "@/common/hooks/useTranslate";
import { ThemedTitle } from "@/canvas/layouts/ThemedBranding/ThemedTitle";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import Image from "@/components/Image/Image";
import { FormTypography } from "@/forms-design-system/FormTypography";

type OrganizationPageHeaderProps = {
  backgroundColor: string;
  logoUrl: Nullable<string>;
  hasBanner: boolean;
  /** If true, disables sticky header behavior (used in preview mode) */
  isPreview?: boolean;
  websiteUrl?: Nullable<string>;
  onWebsiteClick?: () => void;
  slots: {
    organizationName: React.ReactNode;
    mobileButtons?: React.ReactNode;
    desktopButtons?: React.ReactNode;
  };
};

const PHONE_ONLY_DISPLAY = { xs: "flex", sm: "none" };
const DESKTOP_ONLY_DISPLAY = { xs: "none", sm: "flex" };

export const OrganizationPageHeader: FC<OrganizationPageHeaderProps> = ({
  backgroundColor,
  logoUrl,
  hasBanner,
  isPreview = false,
  websiteUrl,
  onWebsiteClick,
  slots,
}) => {
  const { t } = useTranslate();
  const organizationName = slots.organizationName;
  const scrolledFromMobileHook = useHeaderScroll(100);
  const scrolledFromDesktopHook = useHeaderScroll(300);
  const isMobileScrolled = isPreview ? false : scrolledFromMobileHook;
  const isDesktopScrolled = isPreview ? false : scrolledFromDesktopHook || !hasBanner;

  const logoLinkProps =
    websiteUrl && !isPreview
      ? {
          href: websiteUrl,
          target: "_blank" as const,
          rel: "noopener",
          onClick: onWebsiteClick,
          "aria-label": t("common", "a11y.opensInNewTab", { label: websiteUrl }),
        }
      : undefined;

  return (
    <>
      <MobileHeader
        backgroundColor={backgroundColor}
        logoUrl={logoUrl}
        organizationName={organizationName}
        hasBanner={hasBanner}
        isScrolled={isMobileScrolled}
        logoSize={60}
        buttons={slots.mobileButtons}
        logoLinkProps={logoLinkProps}
      />
      <DesktopHeader
        backgroundColor={backgroundColor}
        logoUrl={logoUrl}
        organizationName={organizationName}
        hasBanner={hasBanner}
        isPreview={isPreview}
        isScrolled={isDesktopScrolled}
        logoSize={isDesktopScrolled ? 44 : 90}
        buttons={slots.desktopButtons}
        logoLinkProps={logoLinkProps}
      />
    </>
  );
};

type LogoLinkProps = {
  href: string;
  target: "_blank";
  rel: string;
  onClick?: () => void;
  "aria-label": string;
};

type MobileHeaderProps = {
  backgroundColor: string;
  logoUrl: Nullable<string>;
  organizationName: React.ReactNode;
  hasBanner: boolean;
  isScrolled: boolean;
  logoSize: number;
  buttons: React.ReactNode;
  logoLinkProps?: LogoLinkProps;
};

const MobileHeader: FC<MobileHeaderProps> = ({
  backgroundColor,
  logoUrl,
  organizationName,
  hasBanner,
  isScrolled,
  logoSize,
  buttons,
  logoLinkProps,
}) => {
  const theme = useTheme();

  const mobileLogo = logoUrl ? (
    <Image
      src={logoUrl}
      alt=""
      width={logoSize}
      height={logoSize}
      nextImage
      sx={{
        backgroundColor: "white",
        borderRadius: "9.412px",
        border: `4px solid ${backgroundColor}`,
      }}
    />
  ) : undefined;

  const logoElement = mobileLogo ? (
    logoLinkProps ? (
      <Box component="a" {...logoLinkProps} sx={{ display: "inline-flex", cursor: "pointer" }}>
        {mobileLogo}
      </Box>
    ) : (
      mobileLogo
    )
  ) : undefined;

  return (
    <Stack
      direction="column"
      gap={1}
      alignItems="center"
      sx={{
        display: PHONE_ONLY_DISPLAY,
        position: isScrolled ? "sticky" : "relative",
        top: isScrolled ? 0 : "auto",
        zIndex: 100,
        backgroundColor: isScrolled ? backgroundColor : "transparent",
        marginTop: !isScrolled && mobileLogo && hasBanner ? theme.spacing(-5) : 0,
        marginLeft: isScrolled ? "-20px" : "auto",
        marginRight: isScrolled ? "-20px" : "auto",
        paddingRight: isScrolled ? theme.spacing(1) : 0,
        paddingLeft: isScrolled ? theme.spacing(1) : 0,
        borderRadius: isScrolled ? `0 0 ${theme.radius(3)} ${theme.radius(3)}` : 0,
        boxShadow: isScrolled ? "0 16px 32px 0 rgba(0, 0, 0, 0.12)" : "none",
        textAlign: "center",
        transition: "all 0.4s ease-in-out",
      }}
    >
      {logoElement && (
        <Box
          sx={{
            opacity: isScrolled ? 0 : 1,
            maxHeight: isScrolled ? 0 : `${logoSize}px`,
            overflow: "hidden",
            transition: "opacity 0.4s ease-in-out, max-height 0.4s ease-in-out",
          }}
        >
          {logoElement}
        </Box>
      )}
      <Box
        sx={{
          opacity: isScrolled ? 0 : 1,
          ...(isScrolled ? { maxHeight: 0 } : {}),
          overflow: "hidden",
          transition: "opacity 0.4s ease-in-out, max-height 0.4s ease-in-out",
          "& a": {
            color: "inherit",
            textDecoration: "none",
          },
          "& a:hover": {
            textDecoration: "underline",
            textDecorationColor: theme.palette.forms.text.intense,
          },
        }}
      >
        <FormTypography variant="h3" component="h1" vibe="intense">
          {organizationName}
        </FormTypography>
      </Box>
      {buttons && (
        <Stack
          direction="row"
          gap={1}
          sx={{
            width: isScrolled ? "100%" : "auto",
            justifyContent: isScrolled ? "center" : "flex-start",
            paddingBottom: isScrolled ? theme.spacing(1.5) : 0,
          }}
        >
          {buttons}
        </Stack>
      )}
    </Stack>
  );
};

type DesktopHeaderProps = {
  backgroundColor: string;
  logoUrl: Nullable<string>;
  organizationName: React.ReactNode;
  hasBanner: boolean;
  isPreview: boolean;
  isScrolled: boolean;
  logoSize: number;
  buttons: React.ReactNode;
  logoLinkProps?: LogoLinkProps;
};

const DesktopHeader: FC<DesktopHeaderProps> = ({
  backgroundColor,
  logoUrl,
  organizationName,
  hasBanner,
  isPreview,
  isScrolled,
  logoSize,
  buttons,
  logoLinkProps,
}) => {
  const theme = useTheme();

  const organizationNameWrapperSx = {
    "& a": {
      color: "inherit",
      textDecoration: "none",
    },
    "& a:hover": {
      textDecoration: "underline",
      textDecorationColor: theme.palette.forms.text.intense,
    },
  };

  const renderOrganizationNameTypography = (headingComponent?: "h1") =>
    isScrolled ? (
      <FormTypography variant="h6" component={headingComponent} vibe="intense">
        <Box component="span" sx={organizationNameWrapperSx}>
          {organizationName}
        </Box>
      </FormTypography>
    ) : (
      <ThemedTitle component={headingComponent}>
        <Box component="span" sx={organizationNameWrapperSx}>
          {organizationName}
        </Box>
      </ThemedTitle>
    );

  const logoImage = logoUrl ? (
    <Image
      src={logoUrl}
      alt=""
      width={logoSize}
      height={logoSize}
      nextImage
      sx={{
        backgroundColor: "white",
        borderRadius: "9px",
        boxShadow: `0 0 0 4px ${backgroundColor}`,
        transition: "all 0.4s ease-in-out",
      }}
    />
  ) : undefined;

  const desktopLogo = logoImage ? (
    logoLinkProps ? (
      <Box component="a" {...logoLinkProps} sx={{ display: "inline-flex", cursor: "pointer" }}>
        {logoImage}
      </Box>
    ) : (
      logoImage
    )
  ) : undefined;

  const scrolledStyle = isScrolled
    ? {
        paddingRight: theme.spacing(3),
        paddingLeft: theme.spacing(3),
        marginLeft: `-${theme.spacing(3)}`,
        marginRight: `-${theme.spacing(3)}`,
        borderRadius: theme.radius(2),
        boxShadow: "0 16px 32px 0 rgba(0, 0, 0, 0.12)",
      }
    : {};

  if (!desktopLogo) {
    return (
      <Stack
        direction="row"
        gap={1}
        alignItems="center"
        justifyContent="space-between"
        sx={{
          display: DESKTOP_ONLY_DISPLAY,
          position: isPreview ? "relative" : "sticky",
          top: 0,
          zIndex: 100,
          backgroundColor: isScrolled ? backgroundColor : "transparent",
          padding: isScrolled ? theme.spacing(2, 0) : 0,
          transition: "all 0.4s ease-in-out",
          ...scrolledStyle,
        }}
      >
        {renderOrganizationNameTypography("h1")}
        {buttons && (
          <Stack direction="row" gap={1}>
            {buttons}
          </Stack>
        )}
      </Stack>
    );
  }

  return (
    <>
      <Stack
        direction="row"
        gap={1}
        alignItems={isScrolled ? "center" : "end"}
        sx={{
          display: DESKTOP_ONLY_DISPLAY,
          position: isPreview ? "relative" : "sticky",
          top: 0,
          zIndex: 100,
          backgroundColor: isScrolled ? backgroundColor : "transparent",
          padding: isScrolled ? theme.spacing(2, 0) : 0,
          marginTop: theme.spacing(-5),
          transition: "all 0.4s ease-in-out",
          ...scrolledStyle,
        }}
      >
        {desktopLogo}
        <Box
          sx={{
            position: !isScrolled ? "absolute" : "relative",
            opacity: isScrolled ? 1 : 0,
            transition: "opacity 0.4s ease-in-out, transform 0.4s ease-in-out",
            pointerEvents: isScrolled ? "auto" : "none",
          }}
        >
          {renderOrganizationNameTypography(hasBanner ? undefined : "h1")}
        </Box>
        {buttons && (
          <Stack direction="row" gap={1} sx={{ marginLeft: "auto" }}>
            {buttons}
          </Stack>
        )}
      </Stack>
      {hasBanner && <Box sx={{ display: { xs: "none", sm: "block" } }}>{renderOrganizationNameTypography("h1")}</Box>}
    </>
  );
};
